<template>
  <div class="container mx-auto px-4">
    <section class="pt-10 pb-12 md:pt-16 md:pb-16 max-w-4xl">
      <h1 class="text-3xl md:text-5xl font-orbitron font-semibold leading-tight">
        AI agents, computer vision, and automation tools for technical teams
      </h1>
      <p class="mt-6 text-lg md:text-xl font-roboto text-gray-700 leading-relaxed">
        We build practical AI systems: business agents, synthetic data pipelines, embedded computer-vision models, and
        small automation tools that remove repeated work.
      </p>
      <div class="mt-8 flex flex-wrap gap-4">
        <router-link to="/contact" class="btn-primary">Start a project</router-link>
        <a href="#capabilities" class="btn-secondary">See capabilities</a>
      </div>
      <p class="mt-6 font-roboto text-gray-600">
        Prefer email? <a :href="`mailto:${email}`" class="text-blue-600 hover:text-blue-800 underline">{{ email }}</a>
      </p>
    </section>

    <section class="border-y border-gray-200 py-6">
      <dl class="grid grid-cols-1 gap-6 sm:grid-cols-3 font-roboto">
        <div v-for="fact in facts" :key="fact.label">
          <dt class="text-sm uppercase tracking-wide text-gray-500">{{ fact.label }}</dt>
          <dd class="mt-1 text-lg font-semibold text-gray-900">{{ fact.value }}</dd>
        </div>
      </dl>
    </section>

    <section id="capabilities" class="scroll-mt-24 py-12 md:py-16">
      <h2 class="text-3xl md:text-4xl font-orbitron font-semibold">Capabilities</h2>
      <p class="mt-4 max-w-3xl text-lg font-roboto text-gray-700">
        Five things we are asked for most often. Each one is a defined piece of work with a deliverable you keep.
      </p>

      <div class="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <article v-for="service in services" :key="service.name"
          class="flex flex-col rounded-lg border border-gray-200 p-6 transition-shadow hover:shadow-lg">
          <h3 class="text-xl md:text-2xl font-orbitron font-semibold">{{ service.name }}</h3>
          <dl class="mt-4 flex-grow space-y-3 font-roboto">
            <div v-for="row in serviceRows" :key="row.key">
              <dt class="text-sm uppercase tracking-wide text-gray-500">{{ row.label }}</dt>
              <dd class="text-gray-800 leading-relaxed">{{ service[row.key] }}</dd>
            </div>
          </dl>
          <div class="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-100 pt-4 font-roboto">
            <span class="text-sm text-gray-600">{{ service.timeline }} · {{ service.engagement }}</span>
            <router-link :to="{ path: '/contact', query: { service: service.name } }"
              class="text-blue-600 hover:text-blue-800 underline">Start a project</router-link>
          </div>
        </article>
      </div>
    </section>

    <section class="border-t border-gray-200 py-12 text-center md:py-16">
      <h2 class="text-2xl md:text-3xl font-orbitron font-semibold">Not sure which one fits?</h2>
      <p class="mx-auto mt-4 max-w-2xl text-lg font-roboto text-gray-700">
        Describe the process you want to remove and we will tell you whether it is worth automating — including when the
        answer is no.
      </p>
      <div class="mt-8 flex flex-wrap justify-center gap-4">
        <router-link to="/contact" class="btn-primary">Start a project</router-link>
        <router-link to="/about-us" class="btn-secondary">About the company</router-link>
      </div>
    </section>
  </div>
</template>

<script>
import site from '@/seo';

export default {
  name: 'ServicesComponent',
  data() {
    return {
      email: site.email,
      facts: [
        { label: 'Founded', value: '2004' },
        { label: 'Founder', value: 'István Csiszár' },
        { label: 'Based in', value: 'Nagykanizsa, Hungary' },
      ],
      serviceRows: [
        { key: 'problem', label: 'Problem solved' },
        { key: 'deliverable', label: 'Deliverable' },
        { key: 'example', label: 'Example output' },
      ],
      services: [
        {
          name: 'AI Agent Sprint',
          problem:
            'Your team repeats the same research, triage, or drafting work every week, and it does not get cheaper as you hire.',
          deliverable:
            'A working agent wired into your own tools and data, handed over with its prompts, evaluation set, and deployment scripts.',
          example:
            'A support-triage agent that reads an incoming ticket, pulls the relevant account history, and drafts a reply for a human to approve.',
          timeline: '2–4 weeks',
          engagement: 'Fixed-scope sprint',
        },
        {
          name: 'Synthetic Data and Computer Vision',
          problem:
            'You need a vision model but do not have enough labelled images of what you care about — especially the rare failure cases that matter most.',
          deliverable:
            'A synthetic data pipeline covering 3D scanning, rendering, and automatic labelling, plus a trained model and the generation code.',
          example:
            'Tens of thousands of rendered, pixel-accurate labelled images of a part across varied lighting and poses, and a detector trained on them.',
          timeline: '4–8 weeks',
          engagement: 'Milestone-based project',
        },
        {
          name: 'Embedded ML for IoT Devices',
          problem:
            'A model that runs fine on a workstation will not fit on your device, and shipping every frame to the cloud is too slow or too expensive.',
          deliverable:
            'A quantised model running on the target microcontroller, with firmware integration and the messaging layer wired up.',
          example:
            'A computer-vision model classifying on-device and publishing only the results over MQTT, with no image data leaving the hardware.',
          timeline: '4–8 weeks',
          engagement: 'Milestone-based project',
        },
        {
          name: 'Data Preparation and Labeling',
          problem:
            'Your training data is spread across sources, labelled inconsistently, and nobody trusts the metrics that come out of it.',
          deliverable:
            'A cleaned, merged, labelled dataset with a documented schema and a repeatable pipeline for extending it.',
          example:
            'A deduplicated dataset shipped with a written labelling guide and inter-labeller agreement scores.',
          timeline: '1–4 weeks',
          engagement: 'Fixed-scope sprint or retainer',
        },
        {
          name: 'Rapid Automation Tool Sprint',
          problem:
            'One specific manual process eats hours every week, but it is too small to justify opening a full software project.',
          deliverable:
            'A small single-purpose tool that does exactly that job, deployed, documented, and owned by you.',
          example:
            'An internal web tool that collapses a recurring spreadsheet-and-email routine into a single click.',
          timeline: '1–2 weeks',
          engagement: 'Fixed-scope sprint',
        },
      ],
    };
  },
};
</script>
